import React, { useEffect, useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import HeaderOne from './HeaderOne';
import FooterOne from './BottomFooter';

const ComparisonPage = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const initialMaterialId = searchParams.get('material_id');
    const productTypeId = searchParams.get('product_type_id');
    const customizationDataStr = searchParams.get('customizations');
    const initialWidth = searchParams.get('width') || 36;
    const initialHeight = searchParams.get('height') || 24;

    const [comparisonData, setComparisonData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    const [leftMaterial, setLeftMaterial] = useState(null);
    const [rightMaterial, setRightMaterial] = useState(null);
    const [width, setWidth] = useState(initialWidth);
    const [height, setHeight] = useState(initialHeight);
    const [leftPrice, setLeftPrice] = useState(0);
    const [rightPrice, setRightPrice] = useState(0);

    const [customizations, setCustomizations] = useState({});
    const IMG_BASE_URL = process.env.REACT_APP_IMG_BASE_URL;

    const getImageUrl = (imageUrl) => {
        if (!imageUrl) return 'assets/images/thumbs/default-material.png';
        if (imageUrl.startsWith('http')) return imageUrl;
        if (imageUrl.startsWith('/')) return `${IMG_BASE_URL}${imageUrl}`;
        return `${IMG_BASE_URL}${imageUrl}`;
    };

    useEffect(() => {
        const fetchComparison = async () => {
            if (!initialMaterialId || !productTypeId) {
                setError("Missing comparison parameters");
                setIsLoading(false);
                return;
            }

            try {
                const API_URL = `${process.env.REACT_APP_API_URL}`;
                const customizationData = customizationDataStr ? JSON.parse(decodeURIComponent(customizationDataStr)) : {};

                const response = await axios.post(
                    `${API_URL}/comparisons/compare_materials/`,
                    {
                        product_type_id: parseInt(productTypeId),
                        material_id: parseInt(initialMaterialId),
                        width_inches: parseFloat(initialWidth),
                        height_inches: parseFloat(initialHeight),
                        customization_data: customizationData
                    },
                    { headers: { 'Content-Type': 'application/json' } }
                );

                setComparisonData(response.data);
                setLeftMaterial(response.data.selected_material);

                const initialCustom = {};
                response.data.customizations.forEach((custom, idx) => {
                    initialCustom[idx] = {
                        option_id: custom.selected_option_id,
                        value: custom.selected_value,
                        price_modifier_flat: custom.price_modifier_flat,
                        price_modifier_percent: custom.price_modifier_percent
                    };
                });
                setCustomizations(initialCustom);


                const leftPriceVal = calculatePrice(response.data.selected_material, parseFloat(initialWidth), parseFloat(initialHeight), initialCustom);
                setLeftPrice(leftPriceVal);

                setError(null);
                setIsLoading(false);

            } catch (error) {
                console.error("Error fetching comparison:", error);
                setError(error.response?.data?.error || error.message || "Failed to load comparison");
                setIsLoading(false);
            }
        };

        fetchComparison();
    }, [initialMaterialId, productTypeId, customizationDataStr, initialWidth, initialHeight]);

    const calculatePrice = (material, w, h, customizationMap) => {
        if (!material) return 0;

        const area_sqft = (parseFloat(w) * parseFloat(h)) / 144.0;
        let price = parseFloat(material.cost_per_sqft) * area_sqft;

        Object.values(customizationMap).forEach(custom => {
            if (custom.price_modifier_flat) {
                price += parseFloat(custom.price_modifier_flat);
            }
            if (custom.price_modifier_percent) {
                price += (price * parseFloat(custom.price_modifier_percent) / 100);
            }
        });

        return Math.round(price * 100) / 100;
    };

    const handleCustomizationChange = (fieldIdx, newOption) => {
        const updated = {
            ...customizations,
            [fieldIdx]: newOption
        };
        setCustomizations(updated);

        if (leftMaterial) {
            const newLeftPrice = calculatePrice(leftMaterial, width, height, updated);
            setLeftPrice(newLeftPrice);
        }
        if (rightMaterial) {
            const newRightPrice = calculatePrice(rightMaterial, width, height, updated);
            setRightPrice(newRightPrice);
        }
    };

    const handleDimensionChange = (type, value) => {
        const newValue = parseFloat(value) || 0;
        if (newValue <= 0) return;

        if (type === 'width') setWidth(newValue);
        if (type === 'height') setHeight(newValue);

        const newW = type === 'width' ? newValue : width;
        const newH = type === 'height' ? newValue : height;

        if (leftMaterial) {
            const newLeftPrice = calculatePrice(leftMaterial, newW, newH, customizations);
            setLeftPrice(newLeftPrice);
        }
        if (rightMaterial) {
            const newRightPrice = calculatePrice(rightMaterial, newW, newH, customizations);
            setRightPrice(newRightPrice);
        }
    };

    const handleSelectMaterial = (material) => {
        if (rightMaterial?.id === material.id) {
            setRightMaterial(null);
        } else {
            setRightMaterial(material);
            const newPrice = calculatePrice(material, width, height, customizations);
            setRightPrice(newPrice);
        }
    };

    const handleDropdownSelect = (materialId) => {
        const material = comparisonData.similar_materials.find(m => m.id === parseInt(materialId));
        if (material) {
            handleSelectMaterial(material);
        }
    };

    const areaSize = comparisonData ? Math.round((width * height / 144) * 100) / 100 : 0;

    return (
        <>
            <HeaderOne />

            <div className="comparison-page">
                {isLoading && (
                    <div className="container">
                        <div className="loading-state">
                            <div className="spinner"></div>
                            <p>Loading comparison...</p>
                        </div>
                    </div>
                )}

                {error && !isLoading && (
                    <div className="container py-80">
                        <div className="error-state">
                            <i className="ph ph-warning"></i>
                            <h3>Error</h3>
                            <p>{error}</p>
                            <Link to="/" className="btn btn-primary">Go Back</Link>
                        </div>
                    </div>
                )}

                {!isLoading && !error && comparisonData && (
                    <section className="py-80">
                        <div className="container">
                            <div className="comparison-header">
                                <h2><i className="ph ph-shuffle"></i> Compare Materials</h2>
                                <Link to="/" className="btn btn-primary">
                                    <i className="ph ph-arrow-left"></i> Back
                                </Link>
                            </div>

                            {/* ===== MAIN WRAPPER ===== */}
                            <div className="main-wrapper">
                                {/* LEFT SIDE: Materials + Specifications */}
                                <div className="left-section">
                                    {/* SELECTED MATERIALS */}
                                    <div className="materials-compact-section">
                                        <div className="compact-materials-display">
                                            {/* LEFT MATERIAL */}
                                            <div className="material-compact-card active">
                                                {leftMaterial && (
                                                    <>
                                                        {leftMaterial.image_swatch && (
                                                            <img src={getImageUrl(leftMaterial.image_swatch)} alt={leftMaterial.name} />
                                                        )}
                                                        <h5>{leftMaterial.name}</h5>
                                                        <p className="compact-cost">${leftMaterial.cost_per_sqft}/sq ft</p>
                                                        <div className="compact-price">${leftPrice}</div>
                                                    </>
                                                )}
                                            </div>

                                            {/* VS */}
                                            <div className="compact-vs">VS</div>

                                            {/* RIGHT MATERIAL */}
                                            <div className="material-compact-card">
                                                {rightMaterial ? (
                                                    <>
                                                        {rightMaterial.image_swatch && (
                                                            <img src={getImageUrl(rightMaterial.image_swatch)} alt={rightMaterial.name} />
                                                        )}
                                                        <h5>{rightMaterial.name}</h5>
                                                        <p className="compact-cost">${rightMaterial.cost_per_sqft}/sq ft</p>
                                                        <div className="compact-price">${rightPrice}</div>
                                                        {leftPrice > 0 && (
                                                            <div className={`compact-diff ${rightPrice < leftPrice ? 'save' : 'extra'}`}>
                                                                {rightPrice < leftPrice ? '✓' : '+'} ${Math.abs(rightPrice - leftPrice).toFixed(2)}
                                                            </div>
                                                        )}
                                                        <button
                                                            className="btn-change-compact"
                                                            onClick={() => setRightMaterial(null)}
                                                        >
                                                            Change
                                                        </button>
                                                    </>
                                                ) : (
                                                    <div className="material-select-dropdown">
                                                        <label>Select Material</label>
                                                        <select
                                                            className="material-dropdown-inline"
                                                            onChange={(e) => handleDropdownSelect(e.target.value)}
                                                            defaultValue=""
                                                        >
                                                            <option value="" disabled>Choose Material</option>
                                                            {comparisonData.similar_materials && comparisonData.similar_materials.map((material) => (
                                                                <option key={material.id} value={material.id}>
                                                                    {material.name} - ${material.cost_per_sqft}/sq ft
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* SPECIFICATIONS - BELOW MATERIALS */}
                                    <div className="specifications-section">
                                        <div className="spec-title">Specifications</div>
                                        <div className="spec-controls">
                                            <div className="spec-input">
                                                <label>Width</label>
                                                <input
                                                    type="number"
                                                    value={width}
                                                    onChange={(e) => handleDimensionChange('width', e.target.value)}
                                                    min="1"
                                                />
                                            </div>
                                            <div className="spec-input">
                                                <label>Height</label>
                                                <input
                                                    type="number"
                                                    value={height}
                                                    onChange={(e) => handleDimensionChange('height', e.target.value)}
                                                    min="1"
                                                />
                                            </div>
                                            <div className="spec-area">
                                                <label>Area</label>
                                                <div className="spec-area-value">{areaSize} sq ft</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* RIGHT SIDE: CUSTOMIZATIONS */}
                                {comparisonData.customizations && comparisonData.customizations.length > 0 && (
                                    <div className="customizations-section">
                                        <div className="custom-title">Customizations</div>

                                        {/* CUSTOMIZATION DROPDOWNS */}
                                        <div className="custom-fields">
                                            {comparisonData.customizations.map((custom, idx) => (
                                                <div key={idx} className="custom-field-item">
                                                    <label>{custom.field_name}</label>
                                                    <select
                                                        value={customizations[idx]?.option_id || custom.selected_option_id}
                                                        onChange={(e) => {
                                                            const selectedOpt = custom.options.find(
                                                                opt => opt.id === parseInt(e.target.value)
                                                            );
                                                            handleCustomizationChange(idx, {
                                                                option_id: selectedOpt.id,
                                                                value: selectedOpt.value,
                                                                price_modifier_flat: selectedOpt.price_modifier_flat,
                                                                price_modifier_percent: selectedOpt.price_modifier_percent
                                                            });
                                                        }}
                                                        className="custom-select"
                                                    >
                                                        {custom.options.map((option) => (
                                                            <option key={option.id} value={option.id}>
                                                                {option.value}
                                                                {option.price_modifier_flat > 0 && ` (+$${option.price_modifier_flat})`}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                            ))}

                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* ===== SIMILAR MATERIALS ===== */}
                            {comparisonData.similar_materials && comparisonData.similar_materials.length > 0 && (
                                <>
                                    <div className="section-title">
                                        <h3>Available Materials to Compare</h3>
                                    </div>

                                    <div className="similar-materials-section">
                                        <div className="materials-grid">
                                            {comparisonData.similar_materials.map((material) => (
                                                <div
                                                    key={material.id}
                                                    className={`material-grid-card ${rightMaterial?.id === material.id ? 'selected' : ''}`}
                                                    onClick={() => handleSelectMaterial(material)}
                                                >
                                                    {material.image_swatch && (
                                                        <img src={getImageUrl(material.image_swatch)} alt={material.name} />
                                                    )}
                                                    <h5>{material.name}</h5>
                                                    <p className="grid-cost">${material.cost_per_sqft}/sq ft</p>
                                                    <div className="grid-select-btn">
                                                        {rightMaterial?.id === material.id ? '✓ Selected' : 'Select'}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </section>
                )}
            </div>

            <style jsx>{`
                * {
                    box-sizing: border-box;
                }

                html, body {
                    width: 100%;
                    overflow-x: hidden;
                }

                .comparison-page {
                    background: #f8f9fa;
                    min-height: 100vh;
                    padding: 0;
                    width: 100%;
                    overflow-x: hidden;
                }

                .container {
                    max-width: 1200px;
                    margin: 0 auto;
                    padding: 0 15px;
                    width: 100%;
                    max-width: calc(100% - 30px);
                }

                /* ===== HEADER ===== */
                .comparison-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 40px;
                    flex-wrap: wrap;
                    gap: 15px;
                    width: 100%;
                }

                .comparison-header h2 {
                    margin: 0;
                    font-size: 28px;
                    color: #333;
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    flex: 1;
                    min-width: auto;
                }

                .btn {
                    padding: 10px 20px;
                    border: none;
                    border-radius: 5px;
                    cursor: pointer;
                    background: #d97706;
                    color: white;
                    text-decoration: none;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    font-size: 14px;
                    transition: all 0.3s;
                    white-space: nowrap;
                }

                .btn:hover {
                    background: #b45309;
                }

                /* ===== MAIN WRAPPER - LEFT AND RIGHT ===== */
                .main-wrapper {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 30px;
                    margin-bottom: 50px;
                    width: 100%;
                }

                .left-section {
                    display: flex;
                    flex-direction: column;
                    gap: 20px;
                    width: 100%;
                }

                /* ===== MATERIALS SECTION ===== */
                .materials-compact-section {
                    background: white;
                    border-radius: 12px;
                    padding: 20px;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
                    width: 100%;
                }

                .compact-materials-display {
                    display: grid;
                    grid-template-columns: 1fr auto 1fr;
                    gap: 12px;
                    align-items: center;
                    width: 100%;
                }

                .material-compact-card {
                    background: #f8f9fa;
                    border: 2px solid #e5e7eb;
                    border-radius: 10px;
                    padding: 12px;
                    text-align: center;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 8px;
                    min-height: 200px;
                    justify-content: flex-start;
                    width: 100%;
                }

                .material-compact-card.active {
                    border-color: #d97706;
                    background: #fffbf0;
                }

                .material-compact-card img {
                    width: 100%;
                    height: 100px;
                    object-fit: cover;
                    border-radius: 6px;
                    max-width: 100%;
                }

                .material-compact-card h5 {
                    margin: 0;
                    font-size: 14px;
                    color: #333;
                    font-weight: 600;
                    word-break: break-word;
                    width: 100%;
                }

                .compact-cost {
                    font-size: 12px;
                    color: #666;
                    margin: 0;
                }

                .compact-price {
                    font-size: 18px;
                    font-weight: 700;
                    color: #d97706;
                    margin-top: auto;
                }

                .compact-diff {
                    font-size: 11px;
                    font-weight: 600;
                    padding: 4px 8px;
                    border-radius: 4px;
                    margin-top: 4px;
                }

                .compact-diff.save {
                    background: #d4edda;
                    color: #155724;
                }

                .compact-diff.extra {
                    background: #f8d7da;
                    color: #721c24;
                }

                .compact-vs {
                    font-weight: 700;
                    font-size: 12px;
                    color: #d97706;
                    text-align: center;
                    padding: 0 8px;
                    white-space: nowrap;
                    flex-shrink: 0;
                }

                .material-select-dropdown {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 10px;
                    padding: 20px 10px;
                    min-height: 200px;
                    justify-content: center;
                    width: 100%;
                }

                .material-select-dropdown label {
                    font-size: 14px;
                    font-weight: 600;
                    color: #333;
                }

                .material-dropdown-inline {
                    width: 100%;
                    padding: 10px 8px;
                    border: 2px solid #d97706;
                    border-radius: 6px;
                    font-size: 13px;
                    background: white;
                    cursor: pointer;
                    color: #333;
                }

                .material-dropdown-inline:focus {
                    outline: none;
                    border-color: #b45309;
                    background: #fffbf0;
                }

                .btn-change-compact {
                    padding: 6px 12px;
                    background: #e5e7eb;
                    color: #333;
                    border: none;
                    border-radius: 5px;
                    cursor: pointer;
                    font-size: 11px;
                    font-weight: 600;
                    transition: all 0.3s;
                    margin-top: auto;
                    white-space: nowrap;
                }

                .btn-change-compact:hover {
                    background: #d1d5db;
                }

                /* ===== SPECIFICATIONS SECTION ===== */
                .specifications-section {
                    background: white;
                    border-radius: 12px;
                    padding: 20px;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
                    width: 100%;
                }

                .spec-title {
                    font-size: 16px;
                    font-weight: 700;
                    color: #333;
                    margin-bottom: 15px;
                    border-bottom: 2px solid #d97706;
                    padding-bottom: 10px;
                }

                .spec-controls {
                    display: grid;
                    grid-template-columns: 1fr 1fr 1fr;
                    gap: 15px;
                    width: 100%;
                }

                .spec-input {
                    display: flex;
                    flex-direction: column;
                    gap: 6px;
                    min-width: 0;
                }

                .spec-input label {
                    font-size: 12px;
                    font-weight: 600;
                    color: #333;
                    text-align: left;
                }

                .spec-input input {
                    padding: 10px;
                    border: 2px solid #ddd;
                    border-radius: 6px;
                    font-size: 14px;
                    transition: border-color 0.3s;
                    width: 100%;
                    min-width: 0;
                }

                .spec-input input:focus {
                    outline: none;
                    border-color: #d97706;
                }

                .spec-area {
                    display: flex;
                    flex-direction: column;
                    gap: 6px;
                    justify-content: flex-start;
                    min-width: 0;
                    text-align: left;
                }

                .spec-area label {
                    font-size: 12px;
                    font-weight: 600;
                    color: #333;
                }

                .spec-area-value {
                    padding: 10px;
                    background: #f0f9ff;
                    border-radius: 6px;
                    color: #d97706;
                    font-weight: 600;
                    font-size: 14px;
                    text-align: left;
                    white-space: nowrap;
                }

                /* ===== CUSTOMIZATIONS SECTION ===== */
                .customizations-section {
                    background: white;
                    border-radius: 12px;
                    padding: 20px;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
                    display: flex;
                    flex-direction: column;
                    gap: 15px;
                    height: fit-content;
                    position: sticky;
                    top: 20px;
                    width: 100%;
                }

                .custom-title {
                    font-size: 16px;
                    font-weight: 700;
                    color: #333;
                    border-bottom: 2px solid #d97706;
                    padding-bottom: 10px;
                    margin: 0;
                }

                .custom-fields {
                    display: flex;
                    flex-direction: column;
                    gap: 12px;
                    width: 100%;
                }

                .custom-field-item {
                    display: flex;
                    flex-direction: column;
                    gap: 6px;
                    width: 100%;
                }

                .custom-field-item label {
                    font-size: 12px;
                    font-weight: 600;
                    color: #333;
                }

                .custom-select {
                    padding: 10px;
                    border: 2px solid #ddd;
                    border-radius: 6px;
                    font-size: 13px;
                    background: white;
                    cursor: pointer;
                    transition: border-color 0.3s;
                    width: 100%;
                }

                .custom-select:focus {
                    outline: none;
                    border-color: #d97706;
                }

                .mod-price {
                    font-size: 11px;
                    color: #d97706;
                    font-weight: 600;
                }

                /* ===== SIMILAR MATERIALS ===== */
                .section-title {
                    margin-top: 40px;
                    margin-bottom: 20px;
                    border-bottom: 3px solid #d97706;
                    padding-bottom: 12px;
                }

                .section-title h3 {
                    margin: 0;
                    font-size: 20px;
                    color: #333;
                    font-weight: 700;
                }

                .similar-materials-section {
                    background: white;
                    border-radius: 12px;
                    padding: 25px;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
                    width: 100%;
                }

                .materials-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
                    gap: 20px;
                    width: 100%;
                }

                .material-grid-card {
                    background: white;
                    border: 2px solid #e5e7eb;
                    border-radius: 10px;
                    padding: 15px;
                    cursor: pointer;
                    transition: all 0.3s;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    text-align: center;
                    min-width: 0;
                }

                .material-grid-card:hover {
                    box-shadow: 0 5px 15px rgba(0,0,0,0.1);
                    transform: translateY(-3px);
                }

                .material-grid-card.selected {
                    border-color: #d97706;
                    background: #fffbf0;
                }

                .material-grid-card img {
                    width: 100%;
                    height: 140px;
                    object-fit: cover;
                    border-radius: 8px;
                    margin-bottom: 10px;
                    max-width: 100%;
                }

                .material-grid-card h5 {
                    margin: 8px 0 4px 0;
                    font-size: 14px;
                    color: #333;
                    font-weight: 600;
                    word-break: break-word;
                    width: 100%;
                }

                .grid-cost {
                    color: #666;
                    font-size: 12px;
                    margin: 0 0 10px 0;
                }

                .grid-select-btn {
                    padding: 8px 12px;
                    background: #d97706;
                    color: white;
                    border-radius: 6px;
                    font-weight: 600;
                    font-size: 12px;
                    transition: all 0.3s;
                    width: 100%;
                    margin-top: auto;
                }

                .material-grid-card.selected .grid-select-btn {
                    background: #28a745;
                }

                .loading-state, .error-state {
                    text-align: center;
                    padding: 60px 20px;
                }

                .error-state {
                    background: white;
                    border-radius: 12px;
                    padding: 40px 20px;
                }

                .error-state i {
                    font-size: 48px;
                    color: #d97706;
                    margin-bottom: 16px;
                    display: block;
                }

                .error-state h3 {
                    color: #333;
                    margin: 16px 0 12px 0;
                }

                .error-state p {
                    color: #666;
                    margin-bottom: 20px;
                }

                .spinner {
                    display: inline-block;
                    width: 40px;
                    height: 40px;
                    border: 4px solid #f3f3f3;
                    border-top: 4px solid #d97706;
                    border-radius: 50%;
                    animation: spin 1s linear infinite;
                }

                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }

                /* ===== TABLET BREAKPOINT (1024px) ===== */
                @media (max-width: 1024px) {
                    .main-wrapper {
                        grid-template-columns: 1fr;
                        gap: 25px;
                    }

                    .customizations-section {
                        position: static;
                        top: auto;
                    }

                    .compact-materials-display {
                        grid-template-columns: 1fr auto 1fr;
                    }

                    .compact-vs {
                        font-size: 12px;
                    }

                    .materials-grid {
                        grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
                    }

                    .comparison-header h2 {
                        font-size: 24px;
                    }
                }

                /* ===== TABLET BREAKPOINT (768px) ===== */
                @media (max-width: 768px) {
                    .py-80 {
                        padding: 40px 0;
                    }

                    .container {
                        padding: 0 12px;
                        max-width: calc(100% - 24px);
                    }

                    .comparison-header {
                        margin-bottom: 30px;
                        gap: 10px;
                        width: 100%;
                    }

                    .comparison-header h2 {
                        font-size: 20px;
                        width: 100%;
                        order: 1;
                    }

                    .comparison-header .btn {
                        order: 2;
                        width: 100%;
                        justify-content: center;
                        font-size: 13px;
                        padding: 9px 16px;
                    }

                    .main-wrapper {
                        grid-template-columns: 1fr;
                        gap: 20px;
                        margin-bottom: 30px;
                    }

                    .materials-compact-section {
                        padding: 15px;
                    }

                    .compact-materials-display {
                        grid-template-columns: 1fr auto 1fr;
                        gap: 8px;
                    }

                    .material-compact-card {
                        padding: 10px;
                        gap: 6px;
                        min-height: 180px;
                    }

                    .material-compact-card img {
                        height: 80px;
                    }

                    .material-compact-card h5 {
                        font-size: 13px;
                    }

                    .compact-cost {
                        font-size: 11px;
                    }

                    .compact-price {
                        font-size: 16px;
                    }

                    .compact-diff {
                        font-size: 10px;
                        padding: 3px 6px;
                    }

                    .compact-vs {
                        font-size: 11px;
                        padding: 0 4px;
                    }

                    .material-select-dropdown {
                        padding: 15px 8px;
                        min-height: 160px;
                        gap: 8px;
                    }

                    .material-select-dropdown label {
                        font-size: 13px;
                    }

                    .material-dropdown-inline {
                        font-size: 12px;
                        padding: 8px 6px;
                    }

                    .btn-change-compact {
                        padding: 5px 10px;
                        font-size: 10px;
                    }

                    .specifications-section {
                        padding: 15px;
                    }

                    .spec-title {
                        font-size: 15px;
                        margin-bottom: 12px;
                        padding-bottom: 8px;
                    }

                    .spec-controls {
                        grid-template-columns: 1fr 1fr 1fr;
                        gap: 12px;
                    }

                    .spec-input input {
                        font-size: 13px;
                        padding: 8px;
                    }

                    .spec-area-value {
                        font-size: 13px;
                        padding: 8px;
                        text-align: left;
                    }

                    .customizations-section {
                        padding: 15px;
                        gap: 12px;
                    }

                    .custom-title {
                        font-size: 15px;
                        margin-bottom: 10px;
                        padding-bottom: 8px;
                    }

                    .custom-fields {
                        gap: 10px;
                    }

                    .custom-field-item label {
                        font-size: 11px;
                    }

                    .custom-select {
                        font-size: 12px;
                        padding: 8px;
                    }

                    .mod-price {
                        font-size: 10px;
                    }

                    .section-title {
                        margin-top: 30px;
                        margin-bottom: 15px;
                        padding-bottom: 10px;
                    }

                    .section-title h3 {
                        font-size: 18px;
                    }

                    .similar-materials-section {
                        padding: 15px;
                    }

                    .materials-grid {
                        grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
                        gap: 15px;
                    }

                    .material-grid-card {
                        padding: 12px;
                    }

                    .material-grid-card img {
                        height: 110px;
                    }

                    .material-grid-card h5 {
                        font-size: 13px;
                    }

                    .grid-cost {
                        font-size: 11px;
                    }

                    .grid-select-btn {
                        padding: 6px 10px;
                        font-size: 11px;
                    }
                }

                /* ===== MOBILE BREAKPOINT (600px) ===== */
                @media (max-width: 600px) {
                    .py-80 {
                        padding: 30px 0;
                    }

                    .container {
                        padding: 0 10px;
                        max-width: calc(100% - 20px);
                    }

                    .comparison-header {
                        margin-bottom: 25px;
                        gap: 8px;
                    }

                    .comparison-header h2 {
                        font-size: 19px;
                        width: 100%;
                        order: 1;
                    }

                    .comparison-header .btn {
                        order: 2;
                        width: 100%;
                        font-size: 12px;
                        padding: 8px 14px;
                    }

                    .main-wrapper {
                        grid-template-columns: 1fr;
                        gap: 18px;
                        margin-bottom: 25px;
                    }

                    .left-section {
                        gap: 15px;
                    }

                    .materials-compact-section {
                        padding: 14px;
                        border-radius: 10px;
                    }

                    .compact-materials-display {
                        grid-template-columns: 1fr auto 1fr;
                        gap: 6px;
                    }

                    .material-compact-card {
                        padding: 9px;
                        gap: 5px;
                        min-height: 170px;
                        border-radius: 8px;
                    }

                    .material-compact-card img {
                        height: 75px;
                        border-radius: 5px;
                    }

                    .material-compact-card h5 {
                        font-size: 12px;
                    }

                    .compact-cost {
                        font-size: 10px;
                    }

                    .compact-price {
                        font-size: 16px;
                    }

                    .compact-diff {
                        font-size: 9px;
                        padding: 3px 5px;
                    }

                    .compact-vs {
                        font-size: 10px;
                        padding: 0 3px;
                    }

                    .material-select-dropdown {
                        padding: 14px 8px;
                        min-height: 150px;
                        gap: 8px;
                    }

                    .material-select-dropdown label {
                        font-size: 12px;
                    }

                    .material-dropdown-inline {
                        font-size: 11px;
                        padding: 7px;
                    }

                    .btn-change-compact {
                        padding: 5px 9px;
                        font-size: 9px;
                    }

                    .specifications-section {
                        padding: 14px;
                        border-radius: 10px;
                    }

                    .spec-title {
                        font-size: 14px;
                        margin-bottom: 11px;
                        padding-bottom: 7px;
                    }

                    .spec-controls {
                        grid-template-columns: 1fr 1fr 1fr;
                        gap: 10px;
                    }

                    .spec-input input {
                        font-size: 12px;
                        padding: 7px;
                    }

                    .spec-area-value {
                        font-size: 12px;
                        padding: 7px;
                        text-align: left;
                    }

                    .customizations-section {
                        padding: 14px;
                        border-radius: 10px;
                        gap: 11px;
                    }

                    .custom-title {
                        font-size: 14px;
                        margin-bottom: 9px;
                        padding-bottom: 7px;
                    }

                    .custom-fields {
                        gap: 9px;
                    }

                    .custom-field-item label {
                        font-size: 10px;
                    }

                    .custom-select {
                        font-size: 11px;
                        padding: 7px;
                    }

                    .mod-price {
                        font-size: 9px;
                    }

                    .section-title {
                        margin-top: 25px;
                        margin-bottom: 13px;
                        padding-bottom: 9px;
                    }

                    .section-title h3 {
                        font-size: 17px;
                    }

                    .similar-materials-section {
                        padding: 14px;
                        border-radius: 10px;
                    }

                    .materials-grid {
                        grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
                        gap: 12px;
                    }

                    .material-grid-card {
                        padding: 11px;
                        border-radius: 8px;
                    }

                    .material-grid-card img {
                        height: 100px;
                        border-radius: 6px;
                        margin-bottom: 7px;
                    }

                    .material-grid-card h5 {
                        font-size: 12px;
                    }

                    .grid-cost {
                        font-size: 10px;
                    }

                    .grid-select-btn {
                        padding: 6px 9px;
                        font-size: 10px;
                    }
                }

                /* ===== SMALL MOBILE (420px) ===== */
                @media (max-width: 420px) {
                    .py-80 {
                        padding: 24px 0;
                    }

                    .container {
                        padding: 0 8px;
                        max-width: calc(100% - 16px);
                    }

                    .comparison-header {
                        margin-bottom: 20px;
                    }

                    .comparison-header h2 {
                        font-size: 17px;
                        gap: 6px;
                    }

                    .comparison-header h2 i {
                        font-size: 18px;
                    }

                    .comparison-header .btn {
                        font-size: 11px;
                        padding: 7px 12px;
                        gap: 5px;
                    }

                    .main-wrapper {
                        gap: 16px;
                        margin-bottom: 20px;
                    }

                    .left-section {
                        gap: 14px;
                    }

                    .materials-compact-section {
                        padding: 12px;
                    }

                    .compact-materials-display {
                        gap: 5px;
                    }

                    .material-compact-card {
                        padding: 8px;
                        gap: 4px;
                        min-height: 160px;
                    }

                    .material-compact-card img {
                        height: 70px;
                    }

                    .material-compact-card h5 {
                        font-size: 11px;
                    }

                    .compact-cost {
                        font-size: 9px;
                    }

                    .compact-price {
                        font-size: 15px;
                    }

                    .compact-diff {
                        font-size: 8px;
                        padding: 2px 4px;
                    }

                    .compact-vs {
                        font-size: 9px;
                        padding: 0 2px;
                    }

                    .material-select-dropdown {
                        padding: 12px 6px;
                        min-height: 140px;
                    }

                    .material-select-dropdown label {
                        font-size: 11px;
                    }

                    .material-dropdown-inline {
                        font-size: 10px;
                        padding: 6px;
                    }

                    .btn-change-compact {
                        padding: 4px 8px;
                        font-size: 8px;
                    }

                    .specifications-section {
                        padding: 12px;
                    }

                    .spec-title {
                        font-size: 13px;
                        margin-bottom: 10px;
                        padding-bottom: 6px;
                    }

                    .spec-controls {
                        grid-template-columns: 1fr 1fr 1fr;
                        gap: 8px;
                    }

                    .spec-input input {
                        font-size: 11px;
                        padding: 6px;
                    }

                    .spec-area-value {
                        font-size: 11px;
                        padding: 6px;
                        text-align: left;
                    }

                    .customizations-section {
                        padding: 12px;
                        gap: 10px;
                    }

                    .custom-title {
                        font-size: 13px;
                        padding-bottom: 6px;
                    }

                    .custom-fields {
                        gap: 8px;
                    }

                    .custom-field-item label {
                        font-size: 9px;
                    }

                    .custom-select {
                        font-size: 10px;
                        padding: 6px;
                    }

                    .mod-price {
                        font-size: 8px;
                    }

                    .section-title {
                        margin-top: 20px;
                        margin-bottom: 12px;
                        padding-bottom: 8px;
                    }

                    .section-title h3 {
                        font-size: 15px;
                    }

                    .similar-materials-section {
                        padding: 12px;
                    }

                    .materials-grid {
                        grid-template-columns: repeat(auto-fill, minmax(110px, 1fr));
                        gap: 10px;
                    }

                    .material-grid-card {
                        padding: 10px;
                    }

                    .material-grid-card img {
                        height: 85px;
                        margin-bottom: 6px;
                    }

                    .material-grid-card h5 {
                        font-size: 11px;
                        margin: 3px 0 2px 0;
                    }

                    .grid-cost {
                        font-size: 9px;
                        margin: 0 0 5px 0;
                    }

                    .grid-select-btn {
                        padding: 5px 8px;
                        font-size: 9px;
                    }
                }

                /* ===== EXTRA SMALL MOBILE (360px & Below) ===== */
                @media (max-width: 360px) {
                    .container {
                        padding: 0 6px;
                        max-width: calc(100% - 12px);
                    }

                    .comparison-header h2 {
                        font-size: 15px;
                    }

                    .compact-materials-display {
                        gap: 4px;
                    }

                    .material-compact-card {
                        padding: 7px;
                        gap: 3px;
                        min-height: 150px;
                    }

                    .material-compact-card img {
                        height: 65px;
                    }

                    .material-compact-card h5 {
                        font-size: 10px;
                    }

                    .compact-cost {
                        font-size: 8px;
                    }

                    .compact-price {
                        font-size: 14px;
                    }

                    .compact-vs {
                        font-size: 8px;
                    }

                    .spec-controls {
                        grid-template-columns: 1fr 1fr 1fr;
                        gap: 6px;
                    }

                    .spec-area-value {
                        font-size: 11px;
                        text-align: left;
                    }

                    .materials-grid {
                        grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
                        gap: 8px;
                    }

                    .material-grid-card img {
                        height: 80px;
                    }

                    .section-title h3 {
                        font-size: 14px;
                    }
                }

                /* ===== ULTRA SMALL MOBILE (280px & Below - iPhone SE) ===== */
                @media (max-width: 280px) {
                    .container {
                        padding: 0 4px;
                        max-width: calc(100% - 8px);
                    }

                    .comparison-header h2 {
                        font-size: 13px;
                    }

                    .btn {
                        padding: 6px 8px;
                        font-size: 10px;
                    }

                    .material-compact-card {
                        padding: 6px;
                        min-height: 140px;
                    }

                    .material-compact-card img {
                        height: 60px;
                    }

                    .material-compact-card h5 {
                        font-size: 9px;
                    }

                    .compact-price {
                        font-size: 12px;
                    }

                    .spec-controls {
                        gap: 4px;
                    }

                    .spec-input input,
                    .spec-area-value {
                        font-size: 10px;
                        padding: 5px;
                    }

                    .materials-grid {
                        grid-template-columns: repeat(auto-fill, minmax(90px, 1fr));
                        gap: 6px;
                    }

                    .material-grid-card {
                        padding: 8px;
                    }

                    .material-grid-card img {
                        height: 75px;
                    }

                    .material-grid-card h5 {
                        font-size: 10px;
                    }
                }
            `}</style>

            <FooterOne />
        </>
    );
};

export default ComparisonPage;
